import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Building2, Loader2, Plus, Search, User, UserRound } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { Button, Input } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useAdaptiveDebounce } from "@/lib/debounce";
import { useSetPageHeader } from "@/lib/page-header";
import { useAbrirNaOrganizacao } from "@/lib/use-abrir-na-organizacao";
import { formatPhone } from "@/components/patients/PatientListRow";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";
import { FiltroDeOrganizacoes, useOrganizacoesMarcadas } from "@/components/shared/FiltroDeOrganizacoes";
import { AcaoNaClinica, type AcaoPendente } from "./AcaoNaClinica";
import { AcoesDoPaciente, type OrganizacaoDoPaciente } from "./AcoesDoPaciente";

const LIMITE = 500;

/**
 * /pacientes no acesso pessoal: os pacientes de todas as organizações onde a pessoa pode ver
 * pacientes, filtráveis por organização. A ficha de outra clínica abre só leitura sem trocar a
 * organização ativa; agendar e pedir prótese também. Para editar, "Abrir em" troca (com Desfazer).
 */
export function PacientesDoAcessoPessoal() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { abrir } = useAbrirNaOrganizacao();
  const [busca, setBusca] = useState("");
  const buscaAplicada = useAdaptiveDebounce(busca);
  const [acao, setAcao] = useState<AcaoPendente | null>(null);
  const [novoAberto, setNovoAberto] = useState(false);

  const q = trpc.patient.listMineAcrossOrgs.useQuery({ search: buscaAplicada.trim() || undefined }, { placeholderData: keepPreviousData });
  const organizacoes = useMemo(
    () => [...((q.data?.organizacoes ?? []) as OrganizacaoDoPaciente[])].sort((a, b) => Number(b.pessoal) - Number(a.pessoal)),
    [q.data],
  );
  const doFiltro = useMemo(() => organizacoes.map((o) => ({ id: o.id, nome: o.pessoal ? "Pessoal" : o.nome, pessoal: o.pessoal })), [organizacoes]);
  const filtro = useOrganizacoesMarcadas("pacientes:filtro-de-organizacoes", doFiltro);
  const porId = useMemo(() => new Map(organizacoes.map((o) => [o.id, o])), [organizacoes]);
  const pacientes = (q.data?.pacientes ?? []).filter((p) => filtro.marcadas.has(p.organizationId));
  const total = pacientes.length;

  useSetPageHeader(
    {
      title: "Pacientes",
      subtitle: q.isLoading ? "Carregando..." : `${total} paciente${total === 1 ? "" : "s"} em ${filtro.marcadas.size} organizaç${filtro.marcadas.size === 1 ? "ão" : "ões"}`,
    },
    [total, filtro.marcadas.size, q.isLoading],
  );

  const abrirFicha = (pacienteId: string, o: OrganizacaoDoPaciente) => abrir(o.id, o.nome, `/pacientes/${pacienteId}`);
  const verFicha = (pacienteId: string, o: OrganizacaoDoPaciente) => navigate(o.pessoal ? `/pacientes/${pacienteId}` : `/pacientes/em/${o.id}/${pacienteId}`);
  const iniciar = (tipo: AcaoPendente["tipo"], p: { id: string; name: string; phone: string | null }, o: OrganizacaoDoPaciente) =>
    setAcao({ tipo, organizacaoId: o.id, paciente: p, vez: Date.now() });

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar paciente..." className="pl-9" />
          {q.isFetching && busca && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-zinc-400" />}
        </div>
        <FiltroDeOrganizacoes organizacoes={doFiltro} {...filtro.controle} />
        <Button onClick={() => setNovoAberto(true)} className="ml-auto gap-1.5">
          <Plus className="h-4 w-4" />
          Novo paciente
        </Button>
      </div>

      {q.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
        </div>
      ) : total === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-zinc-200 bg-white py-16 text-center dark:border-border dark:bg-card">
          <UserRound className="mb-2 h-8 w-8 text-zinc-300 dark:text-muted-foreground" />
          <p className="text-sm font-medium text-zinc-900 dark:text-foreground">{busca ? "Nenhum paciente encontrado" : "Nenhum paciente nas organizações marcadas"}</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
          <ul className="divide-y divide-zinc-100 dark:divide-border/60">
            {pacientes.map((p) => {
              const o = porId.get(p.organizationId);
              if (!o) return null;
              return (
                <li
                  key={p.id}
                  onClick={() => verFicha(p.id, o)}
                  className="flex cursor-pointer items-center gap-3 px-4 py-2.5 text-[13px] transition-colors hover:bg-zinc-50 dark:hover:bg-muted/40"
                >
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${p.active ? "bg-emerald-500" : "bg-zinc-300"}`} aria-label={p.active ? "Ativo" : "Inativo"} />
                  <span className="min-w-0 flex-1 truncate font-medium text-zinc-900 dark:text-foreground">{p.name}</span>
                  <span className="hidden w-36 shrink-0 tabular-nums text-zinc-500 dark:text-muted-foreground md:inline">{p.phone ? formatPhone(p.phone) : "—"}</span>
                  <span className="hidden w-40 shrink-0 truncate text-zinc-500 dark:text-muted-foreground lg:inline">{[p.city, p.state].filter(Boolean).join(" - ") || "—"}</span>
                  <span className="inline-flex max-w-[45%] shrink-0 items-center gap-1 rounded-full border border-zinc-200 px-2 py-0.5 text-[11px] font-medium text-zinc-600 dark:border-border dark:text-muted-foreground sm:max-w-[220px]">
                    {o.pessoal ? <User className="h-3 w-3 shrink-0" /> : <Building2 className="h-3 w-3 shrink-0" />}
                    <span className="truncate">{o.pessoal ? "Pessoal" : o.nome}</span>
                  </span>
                  <AcoesDoPaciente
                    nome={p.name}
                    organizacao={o}
                    onVerFicha={() => verFicha(p.id, o)}
                    onAbrirFicha={() => abrirFicha(p.id, o)}
                    onAgendar={() => iniciar("agendar", p, o)}
                    onPedirProtese={() => iniciar("protese", p, o)}
                  />
                </li>
              );
            })}
          </ul>
          {(q.data?.pacientes.length ?? 0) >= LIMITE && (
            <p className="border-t border-zinc-100 px-4 py-2.5 text-center text-[12px] text-zinc-500 dark:border-border dark:text-muted-foreground">
              Mostrando os {LIMITE} primeiros em ordem alfabética. Use a busca para achar os demais.
            </p>
          )}
        </div>
      )}

      {acao && <AcaoNaClinica acao={acao} onFim={() => setAcao(null)} />}
      <PatientFormDialog
        open={novoAberto}
        onClose={() => setNovoAberto(false)}
        onCreated={() => utils.patient.listMineAcrossOrgs.invalidate()}
      />
    </div>
  );
}
